/**
 * Campaign performance table for the Analytics tab — design-system Table +
 * SortableHeader with client-side sort (fine at SMB campaign counts) and the
 * same name/status filters CampaignList offers. Rows toggle the shared
 * cross-widget campaign selection (marketingStore.selectedCampaignIds),
 * which re-scopes KPIs, funnel, and trends.
 */

import { useState } from 'react'
import { cn } from '@/utils'
import { useMarketingStore } from '@/stores/marketingStore'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  SortableHeader,
  Badge,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui'
import { ChannelBadge } from './ChannelBadge'
import { formatMarketingCents } from './format'
import type { CampaignPerformanceRow } from '@/types/marketing'

interface CampaignPerformanceTableProps {
  rows: CampaignPerformanceRow[]
}

type SortKey = 'name' | 'spend' | 'impressions' | 'clicks' | 'conversations' | 'leads' | 'costPerLead' | 'status'

function compare(a: CampaignPerformanceRow, b: CampaignPerformanceRow, key: SortKey): number {
  if (key === 'name' || key === 'status') return (a[key] || '').localeCompare(b[key] || '')
  return (a[key] || 0) - (b[key] || 0)
}

export function CampaignPerformanceTable({ rows }: CampaignPerformanceTableProps) {
  const [sortBy, setSortBy] = useState<SortKey>('spend')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const selectedCampaignIds = useMarketingStore((s) => s.selectedCampaignIds)
  const toggleCampaignId = useMarketingStore((s) => s.toggleCampaignId)

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No campaign data available</p>
  }

  const filtered = rows
    .filter((r) => r.name.toLowerCase().includes(query.toLowerCase()))
    .filter((r) => statusFilter === 'ALL' || r.status === statusFilter)
    .sort((a, b) => {
      const cmp = compare(a, b, sortBy)
      return sortOrder === 'asc' ? cmp : -cmp
    })

  const handleSort = (by: string, order: 'asc' | 'desc') => {
    setSortBy(by as SortKey)
    setSortOrder(order)
  }

  const headerProps = { currentSortBy: sortBy, currentSortOrder: sortOrder, onSort: handleSort }

  return (
    <div>
      <div className="flex items-center gap-2 px-4 pb-3">
        <Input
          placeholder="Filter campaigns..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-9 w-56"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[130px] h-9 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="PAUSED">Paused</SelectItem>
            <SelectItem value="ARCHIVED">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {/* role=grid on the table: rows are selectable, and aria-selected is
          only valid on rows inside a grid/treegrid. */}
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">No campaigns match your filters.</p>
      ) : (
        <Table role="grid" aria-multiselectable="true" containerClassName="max-h-96 overflow-y-auto">
            <TableHeader>
              <TableRow>
                <SortableHeader label="Campaign" sortKey="name" {...headerProps} />
                <SortableHeader label="Spend" sortKey="spend" align="right" {...headerProps} />
                <SortableHeader label="Impressions" sortKey="impressions" align="right" {...headerProps} />
                <SortableHeader label="Clicks" sortKey="clicks" align="right" {...headerProps} />
                <SortableHeader label="Conversations" sortKey="conversations" align="right" {...headerProps} />
                <SortableHeader label="Leads" sortKey="leads" align="right" {...headerProps} />
                <SortableHeader label="CPL" sortKey="costPerLead" align="right" {...headerProps} />
                <SortableHeader label="Status" sortKey="status" align="right" {...headerProps} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row) => {
                const selected = selectedCampaignIds.includes(row.campaignId)
                return (
                <TableRow
                  key={row.campaignId}
                  tabIndex={0}
                  aria-selected={selected}
                  onClick={() => toggleCampaignId(row.campaignId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      toggleCampaignId(row.campaignId)
                    }
                  }}
                  className={cn('cursor-pointer', selected && 'bg-primary/10 hover:bg-primary/15')}
                >
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      <ChannelBadge channel={row.channel} showLabel={false} />
                      {row.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatMarketingCents(row.spend)}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.impressions.toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.clicks.toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.conversations.toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.leads.toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.costPerLead > 0 ? formatMarketingCents(row.costPerLead) : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={row.status === 'ACTIVE' ? 'success' : 'secondary'}>{row.status}</Badge>
                  </TableCell>
                </TableRow>
                )
              })}
            </TableBody>
        </Table>
      )}
    </div>
  )
}
